import {
  isSlaToastNotificationType,
  ManagerNotificationEventData,
  SlaToastNotificationType
} from 'shared/entities/managerNotifications';

import type { SlaToastBurstAction } from './types';

export const SLA_TOAST_BURST_WINDOW_MS = 500;

type SlaToastBurstBucket = {
  count: number;
  representativeData: ManagerNotificationEventData | null;
  timeoutId: ReturnType<typeof setTimeout>;
};

export class SlaToastBurstGateModel {
  private readonly buckets: Map<SlaToastNotificationType, SlaToastBurstBucket> =
    new Map();

  private readonly onAction: (action: SlaToastBurstAction) => void;
  private readonly windowMs: number;

  constructor(
    onAction: (action: SlaToastBurstAction) => void,
    windowMs: number = SLA_TOAST_BURST_WINDOW_MS
  ) {
    this.onAction = onAction;
    this.windowMs = windowMs;
  }

  push = (data: ManagerNotificationEventData): void => {
    if (!isSlaToastNotificationType(data.type)) {
      return;
    }

    const bucket = this.buckets.get(data.type);

    if (!bucket) {
      this.buckets.set(data.type, {
        count: 0,
        representativeData: null,
        timeoutId: this.createTimeout(data.type)
      });
      this.onAction({ kind: 'individual', data });
      return;
    }

    bucket.count += 1;
    bucket.representativeData ??= data;
    this.restartTimeout(data.type, bucket);
  };

  reset = (): void => {
    this.buckets.forEach(({ timeoutId }) => clearTimeout(timeoutId));
    this.buckets.clear();
  };

  private createTimeout = (type: SlaToastNotificationType) =>
    setTimeout(() => this.handleBurstTimeout(type), this.windowMs);

  private restartTimeout = (
    type: SlaToastNotificationType,
    bucket: SlaToastBurstBucket
  ): void => {
    clearTimeout(bucket.timeoutId);
    bucket.timeoutId = this.createTimeout(type);
  };

  private handleBurstTimeout = (type: SlaToastNotificationType): void => {
    const bucket = this.buckets.get(type);

    if (!bucket) {
      return;
    }

    this.buckets.delete(type);

    if (bucket.count === 1 && bucket.representativeData) {
      this.onAction({
        kind: 'individual',
        data: bucket.representativeData
      });
      return;
    }

    if (bucket.count >= 2) {
      this.onAction({
        kind: 'aggregate',
        type,
        count: bucket.count
      });
    }
  };
}
